"use client";

import type { ReactNode } from "react";
import type { NumberResult } from "@/lib/api/numerology";
import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";

type NumberProfile = NonNullable<NumberResult["profile"]>;

// profile.ruling_planet_en (Sun, Moon, ...) lowercases onto the planet
// explainer ids in lib/explainer-catalog-planet-data.ts. Gate on this set so
// only ids with a catalog entry become tappable; the rest stay plain spans.
const HAS_PLANET = new Set([
  "sun",
  "moon",
  "mars",
  "mercury",
  "jupiter",
  "venus",
  "saturn",
  "rahu",
  "ketu",
]);

export function NumberCard({ number, description }: { number: NumberResult; description: string }) {
  const profile = number.profile;

  if (!profile || !profile.ruling_planet_en) {
    return null;
  }

  return (
    <div className="card-sacred p-6 sm:p-8">
      <NumberHeader number={number} description={description} />

      <div className="divider-warm mb-5" />

      <NumberQuickInfo profile={profile} />
      <NumberTraits profile={profile} />
      <LuckyColors profile={profile} />
      <NumberMantra profile={profile} />
    </div>
  );
}

function NumberHeader({ number, description }: { number: NumberResult; description: string }) {
  return (
    <div className="flex items-start gap-4 mb-5">
      <span
        className="flex items-center justify-center w-14 h-14 rounded-xl text-[24px] font-bold shrink-0"
        style={{
          backgroundColor: "var(--ish-accent-light)",
          color: "var(--ish-accent)",
          fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
        }}
      >
        {number.value}
      </span>
      <div>
        <h3
          className="text-[18px] font-bold"
          style={{
            fontFamily: "var(--font-pt-serif), PT Serif, Georgia, serif",
            color: "var(--ish-dark)",
          }}
        >
          {number.label_en}
        </h3>
        <p className="text-[13px]" style={{ color: "var(--ish-text-muted)" }}>
          {number.label_hi} &middot; {description}
        </p>
      </div>
    </div>
  );
}

function NumberQuickInfo({ profile }: { profile: NumberProfile }) {
  const fields = [
    { label: "Lucky Day", value: profile.lucky_days?.[0] ?? "-" },
    { label: "Gemstone", value: profile.lucky_gemstone ?? "-" },
    { label: "Compatible", value: profile.compatible_numbers?.join(", ") ?? "-" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
      <RulingPlanetField profile={profile} />
      {fields.map((field) => (
        <div key={field.label}>
          <SectionLabel className="block mb-1">{field.label}</SectionLabel>
          <span className="text-[14px] font-semibold" style={{ color: "var(--ish-dark)" }}>
            {field.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function RulingPlanetField({ profile }: { profile: NumberProfile }) {
  const handlers = useExplainableHandlers("numerology_result");
  const rulingPlanet = profile.ruling_planet_en ?? "";
  const planetId = rulingPlanet.toLowerCase();
  const hasPlanetExplainer = HAS_PLANET.has(planetId);
  const {
    data: planetData,
    isLoading: planetLoading,
    error: planetError,
    refetch: planetRefetch,
  } = useExplainer("planet", planetId, "medium", { enabled: false });

  return (
    <div>
      <SectionLabel className="block mb-1">Ruling Planet</SectionLabel>
      {hasPlanetExplainer ? (
        <Explainable
          type="planet"
          id={planetId}
          intensity="medium"
          triggerLabel={rulingPlanet}
          data={planetData}
          loading={planetLoading}
          error={planetError ?? null}
          onRequestData={() => void planetRefetch()}
          {...handlers}
          className="text-[14px] font-semibold"
        />
      ) : (
        <span className="text-[14px] font-semibold" style={{ color: "var(--ish-dark)" }}>
          {profile.ruling_planet_en}
        </span>
      )}
      {profile.ruling_planet_hi && (
        <span className="text-[12px] ml-1" style={{ color: "var(--ish-text-faint)" }}>
          {profile.ruling_planet_hi}
        </span>
      )}
    </div>
  );
}

function NumberTraits({ profile }: { profile: NumberProfile }) {
  return (
    <div className="mb-4">
      <SectionLabel className="block mb-2">Key Traits</SectionLabel>
      <div className="flex flex-wrap gap-2">
        {profile.characteristics?.slice(0, 4).map((trait, i) => (
          <span
            key={i}
            className="text-[12px] px-2.5 py-1 rounded-full"
            style={{
              backgroundColor: "var(--ish-accent-light)",
              color: "var(--ish-dark)",
            }}
          >
            {trait}
          </span>
        ))}
      </div>
    </div>
  );
}

function LuckyColors({ profile }: { profile: NumberProfile }) {
  return (
    <div className="flex items-center gap-3">
      <SectionLabel>Lucky Colors</SectionLabel>
      <div className="flex gap-1.5">
        {profile.lucky_colors?.map((color, i) => (
          <span
            key={i}
            className="text-[12px] px-2 py-0.5 rounded-full capitalize"
            style={{
              border: "1px solid var(--ish-border)",
              color: "var(--ish-text-muted)",
            }}
          >
            {color}
          </span>
        ))}
      </div>
    </div>
  );
}

function NumberMantra({ profile }: { profile: NumberProfile }) {
  if (!profile.mantra) {
    return null;
  }

  return (
    <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--ish-border-light)" }}>
      <SectionLabel className="block mb-1">Beej Mantra</SectionLabel>
      <span className="text-[14px] italic" style={{ color: "var(--ish-accent)" }}>
        {profile.mantra}
      </span>
    </div>
  );
}

function SectionLabel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={`text-[11px] font-semibold uppercase tracking-wider ${className}`}
      style={{ color: "var(--ish-text-faint)" }}
    >
      {children}
    </span>
  );
}
